/**
 * The shared step wizard: an ordered list of steps, each with the state its
 * owner computed (from the server or on the client), the active step's
 * panel, and optional Back / Next / Refresh.
 *
 * Workspace setup (SetupScreen) gives each step its own route and SetupGate
 * resumes at the first incomplete one; an integration wizard (Integrations →
 * Microsoft 365) keeps its step in one query parameter and resumes with
 * useWizardQueryStep. A step is { key, label, state, done, available, tone }:
 * `state` is the short line under its name, `done` decides where a wizard
 * resumes, `available: false` locks it, and `tone` (done, failed, attention,
 * neutral; default done when `done`, else neutral) colours its mark and
 * state line. Every mark carries the step's state as words beside it. A
 * step's optional `guideAnchor` (and the wizard's `allStepsAnchor`) marks
 * its rail entry (and the phone's "All steps") for Guide
 * (platform/guide/guideAnchor.js).
 *
 * The step rail is a vertical list on wide screens; below 768px it becomes a
 * progress bar coloured by state and an "All steps" button that opens the
 * same list in a sheet. The panel has an optional header (kicker, title,
 * description, a secondary action), the step's body and a footer with Back,
 * the step's own actions and Next. A step body can put its actions into that
 * footer with useWizardFooter (a portal target), so a view has one action bar.
 */
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { OpsButton, OpsSheet } from '../ui/ops'
import useIsMobile from '../utils/useIsMobile'
import { guideAnchor } from '../guide/guideAnchor'
import './wizard.css'

/** The first step not done; the last step when every step is done. */
export function firstUnfinishedIndex(steps) {
  const index = steps.findIndex((step) => !step.done)
  return index === -1 ? steps.length - 1 : index
}

/**
 * The active step from the query parameter `param`. Without a known step key
 * there, it resumes at the first unfinished step once `ready` (the steps'
 * states are known), replacing the address. `go(index)` opens a step.
 */
export function useWizardQueryStep(steps, { param = 'step', ready = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const index = steps.findIndex((step) => step.key === searchParams.get(param))
  const resumeKey = ready && index === -1 ? steps[firstUnfinishedIndex(steps)].key : null
  const openKey = useCallback((key, options) => setSearchParams((current) => {
    const next = new URLSearchParams(current)
    next.set(param, key)
    return next
  }, options), [param, setSearchParams])

  useEffect(() => {
    if (resumeKey) openKey(resumeKey, { replace: true })
  }, [resumeKey, openKey])

  const go = useCallback((target) => openKey(steps[target].key), [openKey, steps])
  return { index, go }
}

const TONES = new Set(['done', 'failed', 'attention', 'neutral'])

/** A step's tone: its own when given, else done when done, else neutral. */
export function stepTone(step) {
  if (TONES.has(step?.tone)) return step.tone
  return step?.done ? 'done' : 'neutral'
}

function Check() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><polyline points="20 6 9 17 4 12" /></svg>
}
function Cross() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true" focusable="false"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
}

/**
 * The 22px state mark of a step or a setup row: a check when done, a cross
 * when failed, otherwise the step's number (amber when it needs you, an
 * accent ring when it is the active one). The only place a step's number is
 * shown, so a step is never numbered twice.
 */
export function StepMark({ tone = 'neutral', number, active = false }) {
  const kind = tone === 'neutral' && active ? 'active' : tone
  return (
    <span className={`wizard-mark wizard-mark--${kind}`} aria-hidden="true" data-mark={kind}>
      {tone === 'done' ? <Check /> : tone === 'failed' ? <Cross /> : number}
    </span>
  )
}

function StepRail({ label, steps, activeIndex, onSelect }) {
  return (
    <ol className="wizard-rail" aria-label={label}>
      {steps.map((step, index) => {
        const tone = stepTone(step)
        const active = activeIndex === index
        return (
          <li key={step.key} className={`wizard-rail__step wizard-rail__step--${tone}`} data-tone={tone} data-active={active || undefined}>
            <button
              type="button"
              disabled={step.available === false}
              onClick={() => onSelect(index)}
              aria-current={active ? 'step' : undefined}
              className="wizard-rail__button"
              {...guideAnchor(step.guideAnchor)}
            >
              <span className="wizard-rail__track">
                <StepMark tone={tone} number={index + 1} active={active} />
                {index < steps.length - 1 && <span className="wizard-rail__line" aria-hidden="true" />}
              </span>
              <span className="wizard-rail__copy">
                <span className="wizard-rail__name">{step.label}</span>
                {step.state && <span className={`wizard-rail__state wizard-state--${tone}`}>{step.state}</span>}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

/** Below 768px: the progress bar, the current step and "All steps", which opens the list. */
function MobileSteps({ label, steps, activeIndex, onSelect, allStepsAnchor }) {
  const [open, setOpen] = useState(false)
  const opener = useRef(null)
  const close = useCallback(() => setOpen(false), [])
  const active = steps[activeIndex]
  const tone = stepTone(active)
  return (
    <div className="wizard-mobile" data-testid="wizard-mobile-steps">
      <div className="wizard-progress" aria-hidden="true" data-testid="wizard-progress">
        {steps.map((step, index) => {
          const segment = stepTone(step)
          return <span key={step.key} className={`wizard-progress__segment wizard-progress__segment--${segment === 'neutral' && index === activeIndex ? 'active' : segment}`} data-tone={segment} />
        })}
      </div>
      <button ref={opener} type="button" className="wizard-mobile__current" aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid="wizard-all-steps" {...guideAnchor(allStepsAnchor)}>
        <span className="wizard-mobile__count">{activeIndex + 1} / {steps.length}</span>
        <span className="wizard-mobile__name">{active.label}</span>
        {active.state && <span className={`wizard-mobile__state wizard-state--${tone}`}>{active.state}</span>}
        <span className="wizard-mobile__all">All steps</span>
      </button>
      <OpsSheet open={open} title="All steps" onClose={close} returnFocusRef={opener} className="wizard-sheet">
        <StepRail label={label} steps={steps} activeIndex={activeIndex} onSelect={(index) => { setOpen(false); onSelect(index) }} />
      </OpsSheet>
    </div>
  )
}

const FooterSlot = createContext(null)

/** The active step's footer bar (a DOM node for a portal), or null outside a wizard footer. */
export function useWizardFooter() {
  return useContext(FooterSlot)
}

export default function Wizard({
  label, steps, activeIndex, onSelect, allStepsAnchor,
  title, description, kicker, headerAction,
  onBack, onNext, nextLabel = 'Next', nextTone = 'primary', footerNote,
  onRefresh, refreshing = false, children,
}) {
  const mobile = useIsMobile()
  const titleId = useId()
  const [slot, setSlot] = useState(null)
  const nav = Boolean(onBack || onNext || onRefresh)
  const refresh = onRefresh && (
    <OpsButton size="sm" disabled={refreshing} onClick={onRefresh}>{refreshing ? 'Refreshing…' : 'Refresh'}</OpsButton>
  )
  const header = Boolean(title)
  return (
    <div className="wizard">
      {mobile
        ? <MobileSteps label={label} steps={steps} activeIndex={activeIndex} onSelect={onSelect} allStepsAnchor={allStepsAnchor} />
        : <div className="wizard__rail"><StepRail label={label} steps={steps} activeIndex={activeIndex} onSelect={onSelect} /></div>}
      <section className="wizard-panel" aria-labelledby={header ? titleId : undefined} aria-label={header ? undefined : steps[activeIndex]?.label}>
        {header && (
          <header className="wizard-panel__header">
            <div className="wizard-panel__heading">
              <p className="wizard-panel__kicker">{kicker ?? `Step ${activeIndex + 1} of ${steps.length}`}</p>
              <h2 id={titleId} className="wizard-panel__title">{title}</h2>
              {description && <p className="wizard-panel__description">{description}</p>}
            </div>
            {(headerAction || refresh) && <div className="wizard-panel__actions">{headerAction}{refresh}</div>}
          </header>
        )}
        <div className={header ? 'wizard-panel__body' : 'wizard-panel__body wizard-panel__body--plain'}>
          <FooterSlot.Provider value={nav ? slot : null}>{children}</FooterSlot.Provider>
        </div>
        {nav && (
          <footer className="wizard-panel__footer" data-testid="wizard-nav">
            {onBack && <OpsButton size="sm" className="wizard-panel__back" onClick={onBack}>Back</OpsButton>}
            {!header && refresh}
            <span className="wizard-panel__note">{footerNote}</span>
            <span ref={setSlot} className="wizard-panel__slot" />
            {onNext && <OpsButton size="sm" tone={nextTone} onClick={onNext}>{nextLabel}</OpsButton>}
          </footer>
        )}
      </section>
    </div>
  )
}
